import React, { useCallback, useState } from 'react';

import type {
	MessageAction,
	MessageActionInvocationStatus,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { useRovoBridgeAPI } from '@atlassian/jira-create-work-items/useRovoBridgeAPI.tsx';
import { JIRA_PREVIEW_CARD_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/jira-smart-create-modal';
import { JiraCreatedItemsCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/jira-created-items-command';
import { JiraDeclineItemCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/jira-decline-item-command';
import type { RovoContentBridgeSetupHandler } from '@atlassian/rovo-content-bridge-api/create-bridge-client-types';

import {
	MESSAGE_ACTION_RESOLUTIONS,
	MESSAGE_ACTION_INVOCATION_STATUSES,
} from '../../../../common/constants';
import { type ResolveActionsFnc } from '../../../../common/types';
/**
 * Bridges the Rovo Content Bridge API and feeds created issue keys back into the preview card.
 *
 * When issues are created from the modal (via "Create all"), a bridge command is
 * sent with a mapping of action invocation IDs to created issue keys. This component listens for
 * that command and updates the corresponding actions with the created issue keys.
 *
 * Note: after the modal closes, chat messages are refreshed. Because bulk action resolution is
 * not implemented yet, the refreshed data can be temporarily inconsistent (for example, some
 * issues may appear resolved while "resolve actions" queries are still in flight).
 */

type Props = {
	actions: MessageAction[];
	invokedActions?: { [invocationId: string]: MessageActionInvocationStatus };
	children: (props: { actions: MessageAction[] }) => React.ReactNode;
	resolveActions: ResolveActionsFnc;
};

export const ModalSyncBridgeAPIComponent = ({
	actions,
	invokedActions,
	children,
	resolveActions,
}: Props): React.JSX.Element => {
	// TODO: CRCS-4527 After resolveActions is called (e.g., on cancel), the parent's actions prop
	// may not update immediately on the next render. We should fix this.
	// dismissedInvocationIds is used as a workaround to optimistically reflect cancelled
	const [dismissedInvocationIds, setDismissedInvocationIds] = useState<string[]>([]);
	const [invocationIdToCreatedIssueKey, setInvocationIdToCreatedIssueKey] = useState<
		Record<string, string>
	>({});

	const mappedActions = React.useMemo(() => {
		if (
			Object.keys(invocationIdToCreatedIssueKey).length === 0 &&
			dismissedInvocationIds.length === 0
		) {
			return actions;
		}

		const actionsToResolve: MessageAction[] =
			dismissedInvocationIds.length > 0
				? actions.map((action) => ({
						...action,
						status: dismissedInvocationIds.includes(action.invocationId)
							? MESSAGE_ACTION_INVOCATION_STATUSES.cancelled
							: action.status,
					}))
				: actions;

		if (Object.keys(invocationIdToCreatedIssueKey).length === 0) {
			return actionsToResolve;
		}

		const newActions: MessageAction[] = [];
		actionsToResolve.forEach((action) => {
			const createdIssueKey = invocationIdToCreatedIssueKey[action.invocationId];
			if (createdIssueKey) {
				newActions.push({
					...action,
					executedData: {
						createdIssueKey: createdIssueKey,
					},
				});
			}
		});
		return newActions;
	}, [actions, invocationIdToCreatedIssueKey, dismissedInvocationIds]);

	const actionKey = mappedActions[0]?.key;

	const setupHandler: RovoContentBridgeSetupHandler = useCallback(
		(on) => {
			on(JiraCreatedItemsCommand, ({ invocationIdToCreatedIssueKey: invocationIdsMap }) => {
				setInvocationIdToCreatedIssueKey(invocationIdsMap);
			});
			on(JiraDeclineItemCommand, ({ invocationId }) => {
				const workItemsToDecline = [invocationId];
				const summary = // eslint-disable-next-line @typescript-eslint/no-explicit-any
					(actions.find((action) => action.invocationId === invocationId)?.data as any)?.summary as
						| string
						| undefined;

				actions.forEach((action) => {
					if (
						action.invocationId !== invocationId &&
						action.status === MESSAGE_ACTION_INVOCATION_STATUSES.planned &&
						!Object.keys(invokedActions ?? {}).includes(action.invocationId) &&
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						(action.data as any)?.fields?.parent === summary
					) {
						workItemsToDecline.push(action.invocationId);
					}
				});

				resolveActions(actionKey, MESSAGE_ACTION_RESOLUTIONS.cancel, workItemsToDecline);
				setDismissedInvocationIds((prev) => [...prev, ...workItemsToDecline]);
			});
		},
		[actionKey, actions, invokedActions, resolveActions],
	);

	useRovoBridgeAPI({
		clientId: JIRA_PREVIEW_CARD_CLIENT_ID,
		setupHandler: setupHandler,
	});

	return <>{children({ actions: mappedActions })}</>;
};

export function ModalSyncBridgeAPI({
	actions,
	invokedActions,
	children,
	isLastMessage,
	...props
}: Props & { isLastMessage: boolean }): React.ReactElement {
	if (!isLastMessage) {
		// If the message is not the last one with Jira creatable actions,
		// we can skip rendering the bridge and just return the children with the original actions.
		return <>{children({ actions })}</>;
	}

	return (
		<ModalSyncBridgeAPIComponent actions={actions} invokedActions={invokedActions} {...props}>
			{({ actions: mappedActions }) => children({ actions: mappedActions })}
		</ModalSyncBridgeAPIComponent>
	);
}
